import React, { useCallback, useEffect, useState } from 'react';
import { ClientArrivalToolbar } from '../components/reception/ClientArrivalToolbar';
import { DashboardEmployeePage } from './DashboardEmployeePage';
import { useCurrentShop } from '../contexts/CurrentShopContext';
import { CURRENT_SHOP_CHANGED_EVENT } from '../utils/currentShop';
import { MapPin } from 'lucide-react';
import { RefreshWidget } from '../components/RefreshWidget';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import { useRuntimeSettings } from '../hooks/useRuntimeSettings';

const ClientArrivalPage: React.FC = () => {
  const [error, setError] = useState<string | null>(null);
  const [dashboardRefresh, setDashboardRefresh] = useState(0);
  const { currentShopId, currentShop, loading: shopLoading } = useCurrentShop();
  const { autoRefreshIntervalSeconds } = useRuntimeSettings();

  const bumpDashboardRefresh = useCallback(() => {
    setDashboardRefresh((n) => n + 1);
  }, []);

  useEffect(() => {
    window.addEventListener(CURRENT_SHOP_CHANGED_EVENT, bumpDashboardRefresh);
    return () => window.removeEventListener(CURRENT_SHOP_CHANGED_EVENT, bumpDashboardRefresh);
  }, [bumpDashboardRefresh]);

  const { refreshNow, refreshing, lastRefreshAt } = useAutoRefresh({
    intervalSeconds: autoRefreshIntervalSeconds,
    enabled: !shopLoading && !!currentShopId,
    onRefresh: bumpDashboardRefresh,
  });

  return (
    <main className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-display font-bold text-textPrimary">Accueil Client — Piercing</h1>
          <p className="text-sm text-gray-400 mt-1">
            Recherchez ou scannez un client, puis gérez la file d&apos;attente du jour.
          </p>
        </div>
        <div className="inline-flex items-center gap-2 self-start">
          {currentShop && (
            <div className="inline-flex items-center gap-2 text-sm bg-primary-50 text-primary-800 mb-4 border border-primary-100 rounded-lg px-3 py-2">
              <MapPin className="w-4 h-4 flex-shrink-0" />
              <span>
                Shop courant : <strong>{currentShop.name}</strong>
              </span>
            </div>
          )}
          <RefreshWidget
            intervalSeconds={autoRefreshIntervalSeconds}
            refreshing={refreshing}
            lastRefreshAt={lastRefreshAt}
            onRefresh={() => void refreshNow()}
            disabled={shopLoading || !currentShopId}
          />
        </div>
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm" role="alert">
          {error}
        </div>
      )}

      <section aria-label="Tableau de bord réception">
        {!shopLoading && (
          <DashboardEmployeePage
            mode="admin"
            embedded
            refreshToken={dashboardRefresh}
            shopId={currentShopId}
          />
        )}
      </section>
    </main>
  );
};

export default ClientArrivalPage;
